import { business } from "./business-data";
import { services } from "./services-data";
import { locations } from "./locations-data";
import { blogPosts } from "./blog-data";

export interface RouteSeo {
  path: string;
  /** Under 60 characters, keyword-first, ends with the brand name. */
  title: string;
  /** 145–155 characters. */
  description: string;
  changefreq: "daily" | "weekly" | "monthly" | "yearly";
  priority: number;
  lastmod?: string;
}

const BRAND = business.name;
const CITY = business.address.city;
const STATE = business.address.stateShort;
const PHONE = business.phone;

export const staticRoutes: RouteSeo[] = [
  {
    path: "/",
    title: `Handyman in ${CITY}, ${STATE} | ${BRAND}`,
    description:
      `Trusted handyman in ${CITY}, ${STATE}. Drywall, painting, plumbing, electrical, carpentry, decks, and bathroom remodels. Free estimates — call ${PHONE}.`,
    changefreq: "weekly",
    priority: 1.0,
  },
  {
    path: "/about",
    title: `About Our ${CITY} Handyman Team | ${BRAND}`,
    description:
      `Meet ${BRAND} — a local ${CITY} crew doing careful repair, install, and remodel work across the East Bay. English and Spanish. Free estimates.`,
    changefreq: "monthly",
    priority: 0.7,
  },
  {
    path: "/services",
    title: `Handyman Services in ${CITY}, ${STATE} | ${BRAND}`,
    description:
      `Full-service handyman work in ${CITY}: drywall, painting, plumbing, electrical, doors, decks, trim carpentry, and bathroom remodels. Free written quotes.`,
    changefreq: "monthly",
    priority: 0.9,
  },
  {
    path: "/locations",
    title: `Service Areas Near ${CITY}, ${STATE} | ${BRAND}`,
    description:
      `${BRAND} serves ${locations.length} cities within ${business.serviceRadiusMiles} miles of ${CITY}, across the East Bay and Peninsula. Find your area and request a free estimate today.`,
    changefreq: "monthly",
    priority: 0.9,
  },
  {
    path: "/gallery",
    title: `Project Gallery — Our Recent Work | ${BRAND}`,
    description:
      `Browse completed handyman, repair, and remodeling projects by ${BRAND} across ${CITY} and the East Bay. See the finish we work to on every job.`,
    changefreq: "monthly",
    priority: 0.6,
  },
  {
    path: "/blog",
    title: `Home Repair Tips & Guides | ${BRAND}`,
    description:
      `Practical home repair and remodeling advice from a working ${CITY} handyman — drywall, paint, plumbing, electrical, decks, and bathroom planning guides.`,
    changefreq: "weekly",
    priority: 0.8,
  },
];

export const serviceRoutes: RouteSeo[] = services.map((s) => ({
  path: `/services/${s.slug}`,
  title: s.metaTitle,
  description: s.metaDescription,
  changefreq: "monthly",
  priority: 0.9,
}));

export const locationRoutes: RouteSeo[] = locations.map((l) => ({
  path: `/locations/${l.slug}`,
  title: l.metaTitle,
  description: l.metaDescription,
  changefreq: "monthly",
  priority: l.isPrimary ? 0.9 : 0.8,
}));

export const blogRoutes: RouteSeo[] = blogPosts.map((p) => ({
  path: `/blog/${p.slug}`,
  title: p.metaTitle,
  description: p.metaDescription,
  changefreq: "monthly",
  priority: 0.7,
  lastmod: p.isoDate,
}));

export const allRoutes: RouteSeo[] = [
  ...staticRoutes,
  ...serviceRoutes,
  ...locationRoutes,
  ...blogRoutes,
];

const fallback: RouteSeo = {
  path: "*",
  title: `Page Not Found | ${BRAND}`,
  description:
    `The page you are looking for is not available. Browse our handyman services and service areas, or call ${PHONE} for a free estimate.`,
  changefreq: "yearly",
  priority: 0.1,
};

export function getRouteSeo(pathname: string): RouteSeo {
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, "") : "/";
  return allRoutes.find((r) => r.path === normalized) ?? fallback;
}

export const siteUrl = business.siteUrl;
export const ogImage = `${business.siteUrl}${business.ogImage}`;
